import { atom, useAtom } from 'jotai'
import { useCallback, useContext } from 'react'
import { Button } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import UISwitch from '../../../components/ui/Switch'
import { UITooltipTriggerWrapper } from '../../../components/ui/Tooltip'
import { cx } from '../../../utils/cx'
import { track } from '../../../utils/tracker'
import { useTagComplete } from '../store/autoComplete'
import { PromptsCompletionContext } from './PromptsCompletionProvider'

function PromptsCompletionHelpContent() {
  const { t } = useTranslation(['generation'])

  return (
    <div className="p-4 text-sm max-w-[24rem]">
      {t('generation:completion.help.description')}

      <h3 className="mt-2 font-semibold">
        {t('generation:completion.help.keyboard-shortcuts-title')}
      </h3>

      <ul className="ml-4 list-disc">
        <li>
          <span className="font-medium">↑/↓</span>:{' '}
          {t('generation:completion.help.keyboard-shortcuts-up-down')}
        </li>
        <li>
          <span className="font-medium">Tab</span>:{' '}
          {t('generation:completion.help.keyboard-shortcuts-tab')}
        </li>
      </ul>
    </div>
  )
}

const defaultLoadingCompleteAtom = atom(false)

export function PromptsCompletionSwitch(
  props: StyleProps & { labelPlacement?: 'start' | 'end'; disabled?: boolean },
) {
  const { t } = useTranslation(['generation'])
  const { workerController } = useContext(PromptsCompletionContext)
  const { labelPlacement, disabled, ...styleProps } = props

  // Keep subscribing to the worker's loadingComplete atom so the
  // PromptsCompletionProvider gets the same lifecycle as before, even
  // though we no longer swap the visible UI for a progress indicator —
  // the previous progress-bar branch made the toggle look "stuck"
  // because the bar wasn't clickable, and visually diverged from the
  // production button (which is always the standard `UISwitch`).
  useAtom(workerController?.loadingCompleteAtom ?? defaultLoadingCompleteAtom)

  const [enableCompletion, setEnableCompletion] = useTagComplete()

  const onToggle = useCallback(
    (checked: boolean) => {
      track('auto_complete_check', {
        checked,
      })
      setEnableCompletion(checked)
    },
    [setEnableCompletion],
  )

  const label = (
    <UITooltipTriggerWrapper
      delay={250}
      placement="bottom end"
      trigger={
        <Button
          className="font-medium text-xs"
          // Not a standalone Tab stop: the Switch hidden input is the keyboard target.
          excludeFromTabOrder
          onPress={() => !disabled && onToggle(!enableCompletion)}
        >
          {t('generation:completion.label')}
        </Button>
      }
    >
      <PromptsCompletionHelpContent />
    </UITooltipTriggerWrapper>
  )

  return (
    <div
      {...styleProps}
      // flex-row-reverse puts label left of track (labelPlacement='start' default).
      className={cx(
        'flex items-center gap-1 select-none',
        labelPlacement !== 'end' && 'flex-row-reverse',
        props.className,
      )}
    >
      <UISwitch
        className="items-center"
        size="xsmall"
        isSelected={!!enableCompletion}
        onChange={onToggle}
        disabled={disabled}
        SwitchProps={{ 'aria-label': t('generation:completion.label') }}
      />
      {label}
    </div>
  )
}
